import { Link as GatsbyLink, type GatsbyLinkProps } from 'gatsby'
import type { LanguagesType } from '../../../../i18n/i18n'
import { BASE_PATHS } from '../../../../i18n/useLocalizedPath'
import { STORE, usePreferredLanguagesSettingStore } from '../../../../i18n/usePreferredLanguagesSetting'

type BasePath = (typeof BASE_PATHS)[number]

/**
 * Type-safe language path configuration
 * Maps language codes to their root paths:
 * ja: '/'
 * en: '/en'
 */
type LanguagePathConfig = Record<LanguagesType, string> & {
  ja: '/'
  en: '/en'
}

/**
 * Type-safe mapping of language codes to their root paths.
 * Follows the established pattern for managing language preferences:
 * - Japanese paths: Standard paths ('/', '/package/web/', etc.)
 * - English paths: Prefixed paths ('/en/', '/en/package/web/', etc.)
 */
const LANGUAGE_ROOT_PATHS: Readonly<LanguagePathConfig> = {
  ja: '/',
  en: '/en',
} as const

/**
 * Type guard to validate if a path is a base path
 */
const isBasePath = (path: string): path is BasePath => (BASE_PATHS as readonly string[]).includes(path)

/**
 * Helper function to localize a path based on language and BASE_PATHS
 * Implements type-safe path handling with special case for root path
 */
const getLocalizedPath = (path: string, language: LanguagesType): string => {
  // If the language is Japanese, return the path as is
  if (language === STORE.PREFFERRED_LANGUAGE.JAPANESE) {
    return path
  }

  // If the language is English and the path is a base path, add the English prefix
  if (language === STORE.PREFFERRED_LANGUAGE.ENGLISH && isBasePath(path)) {
    return `${LANGUAGE_ROOT_PATHS.en}${path}`
  }

  // Otherwise, return the path as is
  return path
}

type LocalizedLinkProps = Omit<GatsbyLinkProps<Record<string, unknown>>, 'ref'>

/**
 * A language-aware link component that automatically handles path localization
 * based on the user's preferred language setting.
 *
 * Follows established patterns for language path handling:
 * - Uses type-safe language constants from i18n
 * - Handles root path specially ('/' → '/en/')
 * - Maintains BASE_PATHS awareness for proper localization
 * - Implements runtime validation with type guards
 * - Uses TypeScript template literal types for path structure validation
 *
 * @example
 * // Japanese path
 * <LocalizedLink to="/package/web/">ウェブ</LocalizedLink>
 * // Output: <a href="/package/web/">ウェブ</a>
 *
 * @example
 * // English path (automatically adds /en prefix)
 * <LocalizedLink to="/package/web/">Web</LocalizedLink>
 * // Output: <a href="/en/package/web/">Web</a>
 *
 * @param {LocalizedLinkProps} props - Component props including 'to' for the target path
 * @returns {JSX.Element} A localized Gatsby Link component
 */
export const LocalizedLink = ({ to, children, ...rest }: LocalizedLinkProps): JSX.Element => {
  const { preferredLanguage } = usePreferredLanguagesSettingStore()
  const localizedTo = getLocalizedPath(to, preferredLanguage)

  return (
    <GatsbyLink to={localizedTo} {...rest}>
      {children}
    </GatsbyLink>
  )
}
